import React, { useCallback, useEffect, useState } from 'react';
import {
  AppAlert,
  AppButton,
  AppCard,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppModal,
  AppSelect,
  AppSpinner,
  AppStatusBadge,
} from '../ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import { notify } from '../common/AppToast';
import userJetonService from '../../services/userJetonService';

const STATUS_MAP = {
  actif: 'active',
  expire: 'expired',
  revoque: 'revoked',
};

const DUREE_OPTIONS = [
  { value: '', label: 'Jamais' },
  { value: '90d', label: '90 jours' },
  { value: '1y', label: '1 an' },
];

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const UserJetonsCard = ({ userId }) => {
  const { ask, modal } = useAppConfirm();
  const [jetons, setJetons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [nom, setNom] = useState('');
  const [duree, setDuree] = useState('');
  const [nomError, setNomError] = useState(null);
  const [secret, setSecret] = useState(null);

  const loadJetons = useCallback(async () => {
    try {
      setLoading(true);
      setJetons(await userJetonService.list(userId));
    } catch (err) {
      notify.error(err.message || 'Erreur lors du chargement des jetons');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadJetons();
    }
  }, [userId, loadJetons]);

  const closeGenerate = () => {
    setShowGenerate(false);
    setNom('');
    setDuree('');
    setNomError(null);
  };

  const handleGenerate = async () => {
    if (!nom.trim()) {
      setNomError('Le nom du jeton est obligatoire');
      return;
    }
    setGenerating(true);
    setNomError(null);
    try {
      const res = await userJetonService.create(userId, {
        nom: nom.trim(),
        duree: duree || null,
      });
      setSecret(res.data?.valeur || null);
      closeGenerate();
      notify.success('Jeton généré');
      await loadJetons();
    } catch (err) {
      notify.error(err.message || 'Impossible de générer le jeton');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      notify.success('Jeton copié');
    } catch {
      notify.error('Impossible de copier dans le presse-papier.');
    }
  };

  const handleRevoke = async (jeton) => {
    const ok = await ask({
      title: 'Révoquer ce jeton ?',
      message: `La révocation de « ${jeton.nom} » (${jeton.prefixe}…) est immédiate et définitive.`,
      confirmLabel: 'Révoquer',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    try {
      await userJetonService.revoquer(userId, jeton.id);
      notify.success('Jeton révoqué');
      await loadJetons();
    } catch (err) {
      notify.error(err.message || 'Impossible de révoquer le jeton');
    }
  };

  return (
    <>
      <AppCard
        className="kkp-planning-results-card mt-4"
        title="Jetons d'accès"
        subtitle="Identifient la personne auprès des clients IA. Les droits restent ceux du compte."
        actions={(
          <AppButton
            size="sm"
            icon="mdi:key-plus"
            onClick={() => setShowGenerate(true)}
          >
            Générer un jeton
          </AppButton>
        )}
      >
        {secret ? (
          <AppAlert variant="warning" className="mb-3">
            <div className="fw-semibold mb-1">Notez-le maintenant, il ne sera plus affiché.</div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <code className="user-select-all">{secret}</code>
              <AppButton size="sm" variant="outline-secondary" icon="mdi:content-copy" onClick={handleCopy}>
                Copier
              </AppButton>
              <AppButton size="sm" variant="link" onClick={() => setSecret(null)}>
                Masquer
              </AppButton>
            </div>
          </AppAlert>
        ) : null}

        {loading ? (
          <AppSpinner />
        ) : jetons.length === 0 ? (
          <AppEmptyState
            icon="mdi:key-outline"
            message="Aucun jeton pour cet utilisateur."
          />
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Préfixe</th>
                  <th>Création</th>
                  <th>Dernière utilisation</th>
                  <th>Expiration</th>
                  <th>Statut</th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {jetons.map((jeton) => {
                  const revoked = jeton.statut === 'revoque';
                  return (
                    <tr key={jeton.id} style={revoked ? { opacity: 0.55 } : undefined}>
                      <td className="fw-medium">{jeton.nom}</td>
                      <td><code>{jeton.prefixe}</code></td>
                      <td className="text-secondary-light text-sm">{formatDate(jeton.createdAt)}</td>
                      <td className="text-secondary-light text-sm">
                        {jeton.dernierUsage ? formatDate(jeton.dernierUsage) : 'jamais utilisé'}
                      </td>
                      <td className="text-secondary-light text-sm">
                        {jeton.expireLe ? formatDate(jeton.expireLe) : 'jamais'}
                      </td>
                      <td>
                        <AppStatusBadge status={STATUS_MAP[jeton.statut] || 'inactive'} />
                      </td>
                      <td>
                        {jeton.statut === 'actif' ? (
                          <AppButton
                            size="sm"
                            variant="outline-danger"
                            icon="mdi:cancel"
                            title="Révoquer"
                            onClick={() => handleRevoke(jeton)}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AppCard>

      <AppModal
        show={showGenerate}
        title="Générer un jeton"
        onClose={generating ? undefined : closeGenerate}
        closeDisabled={generating}
        footer={(
          <>
            <AppButton variant="outline-secondary" onClick={closeGenerate} disabled={generating}>
              Annuler
            </AppButton>
            <AppButton
              icon="mdi:key-plus"
              loading={generating}
              loadingLabel="Génération..."
              onClick={handleGenerate}
            >
              Générer
            </AppButton>
          </>
        )}
      >
        <AppFormField label="Nom" htmlFor="jeton-nom" required error={nomError}>
          <AppInput
            id="jeton-nom"
            value={nom}
            onChange={(e) => {
              setNom(e.target.value);
              if (nomError) setNomError(null);
            }}
            placeholder="Nom du jeton"
            disabled={generating}
          />
        </AppFormField>
        <AppFormField label="Expiration" htmlFor="jeton-duree">
          <AppSelect
            id="jeton-duree"
            value={duree}
            onChange={(e) => setDuree(e.target.value)}
            disabled={generating}
          >
            {DUREE_OPTIONS.map((opt) => (
              <option key={opt.value || 'jamais'} value={opt.value}>{opt.label}</option>
            ))}
          </AppSelect>
        </AppFormField>
      </AppModal>
      {modal}
    </>
  );
};

export default UserJetonsCard;
